import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { socket } from "../api";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    phone: "",
    password: "",
    otp: "",
    referralCode: "",
  });

  const [errors, setErrors] = useState({
    username: "",
    password: "",
  });
  
  const [myReferralCode, setMyReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0); // seconds remaining for resend

  const [searchParams] = useSearchParams();

  // Prefill referral from URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setFormData((prev) => ({ ...prev, referralCode: ref }));
    }
  }, [searchParams]);

  // Socket listeners for OTP and signup
  useEffect(() => {
    const onOtpResponse = (data) => {
      if (data.success) {
        toast.success("OTP sent successfully!");
        setMyReferralCode(data.referralCode || "");
        setTimer(30); // start 30s countdown
        // Auto-fill OTP only for demo/debug
        // setFormData((prev) => ({ ...prev, otp: data.otp || "" }));
      } else {
        toast.error(data.message || "Failed to send OTP.");
      }
    };

    const onSignupResponse = (data) => {
      setLoading(false);
      if (data.success) {
        toast.success("Signup successful! Redirecting...");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        // Stay on page on failure
        toast.error(data.message || "Signup failed.");
      }
    };

    socket.on("otpResponse", onOtpResponse);
    socket.on("signupResponse", onSignupResponse);

    return () => {
      socket.off("otpResponse", onOtpResponse);
      socket.off("signupResponse", onSignupResponse);
    };
  }, [navigate]);

  // Countdown effect for OTP timer
  useEffect(() => {
    if (timer <= 0) return;

    const intervalId = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    if (name === "username") {
      if (/^[A-Za-z ]*$/.test(value)) {
        setFormData((prev) => ({ ...prev, username: value }));
        setErrors((prev) => ({ ...prev, username: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          username: "Username can contain only letters and spaces.",
        }));
      }
      return;
    }
  
    if (name === "password") {
      setFormData((prev) => ({ ...prev, password: value }));
  
      if (value.length < 4) {
        setErrors((prev) => ({
          ...prev,
          password: "Password must be at least 4 characters.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, password: "" }));
      }
      return;
    }
  
    setFormData((prev) => ({ ...prev, [name]: value }));
  };  
  const validateBeforeOTP = () => {
    if (!formData.username.trim()) {
      toast.error("Please enter your username.");
      return false;
    }
  
    if (!/^[A-Za-z ]+$/.test(formData.username)) {
      toast.error("Username can contain only letters and spaces.");
      return false;
    }
  
    if (!/^09\d{8}$/.test(formData.phone)) {
      toast.error("Phone number must start with 09 and be 10 digits.");
      return false;
    }
  
    if (formData.password.length < 4) {
      toast.error("Password must be at least 4 characters.");
      return false;
    }
  
    return true;
  };
  
  const handleSendOTP = () => {
    if (!validateBeforeOTP()) return;
  
    socket.emit("sendOTP", { phone: formData.phone });
  };
  

  const handleSignup = (e) => {
    e.preventDefault();

    if (
      !formData.username ||
      !formData.phone ||
      !formData.password ||
      !formData.otp
    ) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (formData.password.length < 4) {
      toast.error("Password must be at least 4 characters.");
      return;
    }
    setLoading(true);
    socket.emit("signup", formData);
  };
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };
  return (
    <div className="min-h-screen bg-primary text-white flex justify-center items-center px-4 py-6">
      <div className="w-full max-w-md bg-white/20 backdrop-blur-xl rounded-xl shadow-lg p-6 sm:p-8">
        <h1 className="text-2xl sm:text-3xl font-semibold mb-3 text-center text-white font-serif">
          Create New Account
        </h1>

        <p className="text-gray-300 mb-6 text-center text-sm sm:text-base font-serif">
          Already Registered?{" "}
          <a
            href="/login"
            className="text-blue-400 hover:text-blue-500 underline font-serif"
          >
            Login here
          </a>
        </p>

        <form onSubmit={handleSignup} className="space-y-4 text-left">
          {/* Username */}
          <div>
            <label className="text-gray-200 text-sm font-serif">Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter Username"
              className="mt-1 w-full bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none text-sm sm:text-base"
            />
            {errors.username && (
              <p className="text-red-400 text-xs mt-1 font-serif">
                {errors.username}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="text-gray-200 text-sm font-serif">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,10}$/.test(value)) {
                  setFormData((prev) => ({ ...prev, phone: value }));
                }
              }}
              placeholder="Enter Phone Number"
              className="mt-1 w-full bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none text-sm sm:text-base"
            />
          </div>

          {/* Password */}
          <div>
            <label className="text-gray-200 text-sm font-serif">Password</label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter Password"
                className="mt-1 w-full bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none text-sm sm:text-base"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill={showPassword ? "#000000" : " #058f05ff"} // Dynamic color based on state
                className="w-[18px] h-[18px] absolute right-2 cursor-pointer"
                onClick={togglePasswordVisibility}
                viewBox="0 0 128 128"
              >
                <path d="M64 16c-32 0-58.4 20.8-64 48 5.6 27.2 32 48 64 48s58.4-20.8 64-48c-5.6-27.2-32-48-64-48zm0 80c-17.6 0-32-14.4-32-32s14.4-32 32-32 32 14.4 32 32-14.4 32-32 32zm0-48c-8.8 0-16 7.2-16 16s7.2 16 16 16 16-7.2 16-16-7.2-16-16-16z" />
              </svg>
            </div>
            {errors.password && (
  <p className="text-red-400 text-xs mt-1 font-serif">
    {errors.password}
  </p>
)}
          </div>

          {/* OTP + Send */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 gap-3">
            <input
              type="text"
              name="otp"
              value={formData.otp}
              onChange={handleChange}
              placeholder="OTP"
              className="flex-1 bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none text-sm sm:text-base"
            />

<button
  type="button"
  onClick={handleSendOTP}
  disabled={
    timer > 0 ||
    !formData.username.trim() ||
    !/^[A-Za-z ]+$/.test(formData.username) ||
    !/^09\d{8}$/.test(formData.phone) ||
    formData.password.length < 4
  }
  className={`transition px-4 py-2 sm:py-3 rounded-md font-medium text-sm sm:text-base ${
    timer > 0 ||
    !formData.username.trim() ||
    !/^[A-Za-z ]+$/.test(formData.username) ||
    !/^09\d{8}$/.test(formData.phone) ||
    formData.password.length < 4
      ? "bg-gray-600 cursor-not-allowed text-white"
      : "bg-gradient-to-br from-primary to-cyan-600 text-white"
  }`}
>
  {timer > 0 ? `Resend in ${timer}s` : "Send OTP"}
</button>

          </div>

          {/* Referral Code */}
          <div>
            <label className="text-gray-200 text-sm font-serif">
              Referral Code (Optional)
            </label>
            <input
              type="text"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleChange}
              placeholder="Enter friend's referral code (optional)"
              className="mt-1 w-full bg-primary border border-[#2c4e74] text-white rounded-md px-3 py-2 sm:py-3 focus:ring-2 focus:ring-blue-400 outline-none text-sm sm:text-base"
            />
          </div>

          {/* Your referral code */}
          {myReferralCode && (
            <div className="bg-[#0e2b48] text-sm text-gray-300 p-3 rounded-md border border-[#2c4e74]">
              🎁 <strong>Your Code:</strong> {myReferralCode}
              <p className="text-xs text-gray-400 mt-1">
                Share this code — you’ll earn 2 birr for each signup.
              </p>
            </div>
          )}

          {/* Signup Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 sm:py-3 rounded-md text-white font-semibold mt-4 transition text-sm sm:text-base ${
              loading
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-gradient-to-r from-red-500 to-cyan-600 hover:opacity-90"
            }`}
          >
            {loading ? "Signing up..." : "Sign Up  | ይመዝገቡ"}
          </button>
        </form>
      </div>

      {/* Toast Popup */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
};

export default Signup;
